<script lang="ts" context="module">
    const resizeObserver =
        typeof window !== 'undefined' && window.ResizeObserver
            ? new window.ResizeObserver((entries) => {
                  for (const entry of entries) {
                      // Fall back to `contentRect` for Safari < 15.4 compatibility.
                      // (Note that this requires `observe` to be called with the
                      // `{ box: 'border-box' }` option, otherwise we will get the
                      // content box height instead.)
                      const height =
                          entry?.borderBoxSize[0].blockSize ??
                          entry.contentRect.height;

                      const event = new CustomEvent<VirtualScrollResizeDetail>(
                          'virtualScrollResize',
                          { detail: { height } },
                      );

                      entry.target.dispatchEvent(event);
                  }
              })
            : null;
</script>

<script lang="ts">
    import { onMount, createEventDispatcher } from 'svelte';
    import { throttle } from '@amp/web-app-components/src/utils/throttle';
    import { splitIntoChunks } from '@amp/web-app-components/src/utils/splitIntoChunks';
    import {
        useActions,
        type ActionArray,
    } from '@amp/web-app-components/src/utils/use-actions';

    // This type alias is used to refer to the generic items that the component will render.
    // Although it is unknown, it's a specific kind of unknown.
    type UnknownItem = unknown;

    // Generic type for pageMetadata export
    type PageMetadata = {
        next?: string;
        page?: number;
        type?: string;
    };

    // When manually generating pagination requests, if the total number of items
    // on the page is less than or equal this threshold, then all items are requested
    // at once.
    const LOAD_FULL_PAGE_THRESHOLD = 500;

    const dispatch = createEventDispatcher();

    export let items: any[];
    export let itemCount: number;

    export let itemHeight: number;
    export let amountToFetch: number;
    export let bottomSpacerHidden: boolean = false;
    export let bufferHeight: number;
    export let fetchIntentFn: ({}: unknown) => unknown;
    export let selectedItemId: any | null = null;
    export let log: any;
    export let jet: any;
    export let overrideKeydown: Function | null = null;
    export let restrainContainerWidth: boolean = false;
    export let rowZebraStriping: boolean = false;
    export let use: ActionArray = [];

    // Sometimes an API may allow for pagination of scroll lists but not
    // provide an expected total item count.  In these cases, a default
    // estimated count would be providecd, which might be a lot more than
    // the amount of data available.  In these cases, allow sending a dispatch
    // with the final count so the parent component can update it.
    export let allowCountUpdates: boolean = false;

    // Manually creating pagination is used by Music library pages
    // where pagination is not suppoprted by Music API
    export let useManualPagination: boolean = true;

    // Page metadata supplied by a page that does support pagination
    // from Media API.  This is used for setting up the first page
    // request.
    export let pageMetadata: PageMetadata | null = null;

    // Adds ARIA roles and attributes to the virtual scroll container and
    // virtual scroll rows. This is needed for track lists within the
    // virtual scroll container to remain accessible to VoiceOver in the
    // same way as catalog tracklists.
    export let setAriaAttributes: boolean = false;

    let focusedIndex: number = 0;

    /*
     * Variables used by Media API-supported pagination
     */
    $: next = pageMetadata?.next;

    /*
     * The element used for virtual scrolling.
     */
    let scrollContainer: HTMLElement;

    /*
     * Height in pixels of the virtual scroll container (corresponds to `offsetHeight`).
     */
    let scrollContainerHeight: number = 0;

    /*
     * Current scroll position in pixels, as measured from top of virtual scroll
     * container (corresponds to `scrollTop`).
     */
    let scrollContainerOffset: number = 0;

    /*
     * Page promise cache, used to populate items on scroll. Whenever we fetch a page,
     * we store the promise in this cache. If we have already fetched a page, the
     * promise will resolve immediately.
     */
    let pageCache: Promise<UnknownItem[]>[];

    // When `items` changes (e.g. an album is deleted), reinitialize page cache
    // to align with updated item offsets. (This should only trigger when `items`
    // is reassigned on page invalidation, and not when an individual item is
    // added to `items` via index assignment.)
    $: pageCache = splitIntoChunks(items, amountToFetch).map(
        (pageItems, pageIdx) => {
            // Filter out any incomplete pages that have been invalidated. The page
            // will be refetched the next time an item from that page needs rendering.
            const isLastPage = (pageIdx + 1) * amountToFetch;
            const expectedPageSize = isLastPage
                ? amountToFetch
                : itemCount % amountToFetch;
            if (!(pageItems.length < expectedPageSize)) {
                return new Promise((resolve) => resolve(pageItems));
            }
        },
    );

    // Item IDs for every row
    $: allRows = [...Array(itemCount).keys()];

    // Total height of virtual scroll container in pixels
    $: allRowsHeight = itemHeight * allRows.length;

    // Use scroll offset, scroll container size, and buffer size to determine
    // the first and last virtual rows to render.
    $: scrollTop = Math.max(0, scrollContainerOffset - bufferHeight);
    $: scrollBottom = Math.min(
        scrollContainerOffset + scrollContainerHeight + bufferHeight,
        allRowsHeight,
    );
    $: firstVisibleRowIdx = visibleRowIdxAtPosition(
        'top',
        scrollTop,
        itemHeight,
    );
    $: lastVisibleRowIdx = visibleRowIdxAtPosition(
        'bottom',
        scrollBottom,
        itemHeight,
    );

    // Gather the data (items) for all visible rows.
    $: visibleRows = allRows
        .slice(firstVisibleRowIdx, lastVisibleRowIdx)
        .map((itemIdx) => items[itemIdx] ?? fetchAndCacheItem(itemIdx));

    // Calculate bounds of previous and next view buffer
    $: visibleRowsHeight = scrollContainerHeight + 2 * bufferHeight;
    $: topRowIdxToFetch = visibleRowIdxAtPosition(
        'top',
        Math.max(0, scrollTop - visibleRowsHeight),
        itemHeight,
    );
    $: bottomRowIdxToFetch = visibleRowIdxAtPosition(
        'bottom',
        Math.min(scrollBottom + visibleRowsHeight, allRowsHeight),
        itemHeight,
    );

    // Preemptively fetch and cache items in surrounding view buffers
    $: {
        if (topRowIdxToFetch > 0 && bottomRowIdxToFetch > 0) {
            allRows
                .slice(topRowIdxToFetch, firstVisibleRowIdx)
                .forEach((itemIdx) => {
                    if (!items[itemIdx]) fetchAndCacheItem(itemIdx);
                });

            allRows
                .slice(lastVisibleRowIdx, bottomRowIdxToFetch)
                .forEach((itemIdx) => {
                    if (!items[itemIdx]) fetchAndCacheItem(itemIdx);
                });
        }
    }

    const visibleRowIdxAtPosition = (
        location: 'top' | 'bottom',
        position: number,
        height: number,
    ): number => {
        const roundFn = location === 'top' ? Math.floor : Math.ceil;
        return roundFn(position / height);
    };

    const fetchAndCacheItem = async (itemIdx: number): Promise<UnknownItem> => {
        const pageIdx = Math.floor(itemIdx / amountToFetch);
        const itemPageIdx = itemIdx % amountToFetch;

        // Check page cache -- we might already be fetching (or may have already fetched) this page.
        if (!pageCache[pageIdx]) {
            const offsetToFetch = pageIdx * amountToFetch;

            // Cache the promised items for this page.
            pageCache[pageIdx] = (async () => {
                await pageCache?.[pageIdx - 1];
                const { items } = await fetchItems(offsetToFetch);
                return items;
            })();
        }

        try {
            const page = await pageCache[pageIdx];
            const item = page[itemPageIdx];

            // Cache the item itself, so we can bypass the page cache in the future.
            if (item) {
                items[itemIdx] = item;
            }

            return item;
        } catch {
            // If page fails to fetch (promise is rejected), remove this entry from
            // the page cache. A placeholder will be rendered for the item, and the
            // next time the view is updated (i.e. on scroll), this function will be
            // called and attempt to fetch and cache the page again.
            delete pageCache[pageIdx];

            return null;
        }
    };

    /**
     *
     * Fetches next page of items during infinate scroll pagination.  `offset` param
     * is only passed in when Media API does not support pagination for a particular
     * endpoint and you need to manually calculate the offset.
     *
     * @method fetchItems
     * @param {number[]} offset Indicates the starting point of the next pagination cursor
     * @returns {PaginationResponse}
     */
    const fetchItems = async (offset?: number): Promise<any> => {
        if (useManualPagination) {
            try {
                const itemsIntent = fetchIntentFn({
                    offset,
                    limit: amountToFetch,
                });

                const { items, next, total } = await jet.dispatch(itemsIntent);

                // Used to update itemSelector for library songs in Music
                dispatch('fetchComplete', { items, offset });

                return { items, next, total };
            } catch (e) {
                log.error('Loading Paginated Items Failed', e);
                return {};
            }
        } else {
            if (next) {
                // Intent must be created outside of the try block otherwise
                // it will error saying that the `next` value is not
                // accessible before initialization.
                const itemsIntent = fetchIntentFn({
                    path: next,
                    limit: amountToFetch,
                });

                try {
                    const result = await jet.dispatch(itemsIntent);

                    // Update the stored next url value
                    next = result.next;

                    const { items } = result;

                    dispatch('fetchComplete', { items, offset });

                    return { items, next };
                } catch (e) {
                    log.error('Loading Paginated Items Failed', e);
                    return {};
                }
            } else if (allowCountUpdates) {
                log.warn(
                    'No more pages to fetch, updating scroll list total count.',
                );
                dispatch('updateItemsCount', items.length);
            }
        }
    };

    const virtualScrollResizeObserver = (node: HTMLElement) => {
        if (resizeObserver) {
            resizeObserver.observe(node, { box: 'border-box' });

            return {
                destroy: () => {
                    resizeObserver.unobserve(node);
                },
            };
        }
    };

    const handleScrollContainerResize = (
        e: CustomEvent<VirtualScrollResizeDetail>,
    ) => {
        const { height } = e.detail;
        scrollContainerHeight = height;
    };

    const scrollHandler = (): void => {
        scrollContainerOffset = scrollContainer?.scrollTop;
    };

    function handleClick(
        event: CustomEvent<{ item: any }>,
        index: number,
    ): void {
        const { item } = event.detail;
        const { id } = item;
        selectedItemId = id;
        focusedIndex = index;
        dispatch('select', {
            id: selectedItemId,
            scrollTop: scrollContainer.scrollTop,
        });
    }

    const handleIndexedClick = (index: number) => {
        return (event: CustomEvent<{ item: any }>) => handleClick(event, index);
    };

    const shiftTabKeys = {
        shift: false,
        tab: false,
    };

    const setShiftTab = (key: string, isKeyed: boolean) => {
        if (key === 'Shift') {
            shiftTabKeys.shift = isKeyed;
        } else if (key === 'Tab') {
            shiftTabKeys.tab = isKeyed;
        }
    };

    const isShiftTabKeyed = () => shiftTabKeys.shift && shiftTabKeys.tab;

    function handleKeyUp(event: KeyboardEvent): void {
        if (event.key === 'Tab' || event.key === 'Shift') {
            event.preventDefault();
            setShiftTab(event.key, false);
        }
    }

    function handleKeyDown(event: KeyboardEvent): void {
        if (overrideKeydown) {
            overrideKeydown(event);
            return;
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
        }

        // Don't prevent default when pressing tab on the last item or shift+tab on the first one
        const pressingShiftTabOnFirstElement =
            event.key === 'Tab' && event.shiftKey && focusedIndex === 0;
        const pressingTabOnLastElement =
            event.key === 'Tab' &&
            !event.shiftKey &&
            focusedIndex === items.length - 1;
        const aboutToTabOutOfList =
            pressingShiftTabOnFirstElement || pressingTabOnLastElement;
        if (event.key === 'Tab' && !aboutToTabOutOfList) {
            event.preventDefault();
        }

        setShiftTab(event.key, true);

        switch (event.key) {
            case 'ArrowUp':
                focusedIndex = Math.max(focusedIndex - 1, 0);
                break;
            case 'ArrowDown':
                focusedIndex = Math.min(focusedIndex + 1, items.length - 1);
                break;
            case 'Tab':
                if (isShiftTabKeyed()) {
                    focusedIndex = Math.max(focusedIndex - 1, 0);
                } else {
                    focusedIndex = Math.min(focusedIndex + 1, items.length - 1);
                }
                break;
            default:
        }

        if (items[focusedIndex]?.id) {
            selectedItemId = items[focusedIndex].id;
            dispatch('select', {
                id: selectedItemId,
                scrollTop: scrollContainer.scrollTop,
            });
        }
    }

    onMount(() => {
        const nextOffset = amountToFetch;

        // If total items are less than next offset pointer,
        // then no need to call pagination request
        if (itemCount <= nextOffset) {
            return;
        }

        // If page items are smaller than this threshold, fetch and cache all items.
        // Only done when manually generating pagination requests.
        else if (useManualPagination && itemCount <= LOAD_FULL_PAGE_THRESHOLD) {
            const limit = Math.min(itemCount, LOAD_FULL_PAGE_THRESHOLD);
            // Note: this will populate both the page cache *and* item cache!
            for (let i = nextOffset; i < limit; i++) {
                fetchAndCacheItem(i);
            }
        }

        // Otherwise, fetch the next page of data
        else {
            // Only one item is needed to trigger the page fetch
            fetchAndCacheItem(nextOffset);
        }
    });
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div
    class="container"
    data-testid="container"
    class:scrollable-container-constrained-width={restrainContainerWidth}
    on:keydown={handleKeyDown}
    on:keyup={handleKeyUp}
    use:useActions={use}
    tabindex="0"
>
    <div
        class="scrollable-container"
        id="scrollable-page-override"
        bind:this={scrollContainer}
        on:scroll={throttle(scrollHandler, 10)}
        use:virtualScrollResizeObserver
        on:virtualScrollResize={handleScrollContainerResize}
        style:contain="layout"
    >
        <div
            class="virtual-rows"
            data-testid="virtual-rows"
            class:row-zebra-striping={rowZebraStriping}
            style:min-height="{allRowsHeight}px"
            style:--rowHeight="{itemHeight}px"
            role={setAriaAttributes ? 'grid' : null}
            aria-rowcount={setAriaAttributes ? itemCount : null}
        >
            {#each visibleRows as itemOrPromise, itemIdx (itemOrPromise)}
                {@const offsetItemIdx = firstVisibleRowIdx + itemIdx}
                {@const offsetFromTop = offsetItemIdx * itemHeight}
                <div
                    class="virtual-row"
                    data-testid="virtual-row"
                    style:contain="strict"
                    style:top="{offsetFromTop}px"
                    style:min-height="{itemHeight}px"
                    role={setAriaAttributes ? 'rowgroup' : null}
                >
                    {#await itemOrPromise}
                        <slot name="placeholder" itemIdx={offsetItemIdx} />
                    {:then item}
                        <slot
                            name="itemComponent"
                            {item}
                            itemIdx={offsetItemIdx}
                            visibleItems={visibleRows}
                            isSelected={selectedItemId === item?.id}
                            handleClick={handleIndexedClick(itemIdx)}
                        />
                    {/await}
                </div>
            {/each}
        </div>

        <div
            class:bottom-spacer-hidden={bottomSpacerHidden}
            style:height={'10px'}
        />
    </div>
</div>

<style lang="scss">
    .scrollable-container {
        position: absolute;
        overflow-y: scroll;
        height: calc(100% - var(--fixedHeaderHeight, 0px));
        width: 100%;
        z-index: var(--z-default);

        @media (--range-sidebar-hidden-down) {
            // TODO: Revisit `padding-bottom` as part of
            // rdar://98057841 (JMOTW: Mobile scrolling and content real estate should be improved)
            padding-bottom: 180px;
        }
    }

    .scrollable-container-constrained-width {
        position: relative;
        height: 100%;
        min-width: 240px;
        width: 100%;
        border-inline-end: 1px solid var(--labelDivider);
        z-index: var(--z-default);

        @media (--small) {
            flex: 0;
            width: 30%;
        }
    }

    .row-zebra-striping {
        background-image: linear-gradient(
            0deg,
            transparent 25%,
            var(--trackBackgroundEven) 25%,
            var(--trackBackgroundEven) 50%,
            transparent 50%,
            transparent 75%,
            var(--trackBackgroundEven) 75%,
            var(--trackBackgroundEven) 100%
        );
        background-size: auto calc(var(--rowHeight) * 4);
    }

    .virtual-row {
        position: absolute;
        width: 100%;
        box-sizing: border-box;
        overflow: hidden;
        z-index: var(--z-default);
    }

    .container {
        &:focus-visible {
            outline: none;
        }
    }

    .bottom-spacer-hidden {
        display: none;
    }
</style>
